/**
 * The Product Q&A → FogBugz Formulator hand-off (epic PI-SPEC-SW-E310,
 * feature PI-SPEC-SW-1631, ER-1667 Package F Resolved Decision 10c; contract
 * ask 1, additive).
 *
 * "Start FogBugz Formulator" on a Product Q&A answer opens the Toolbox
 * FogBugz Formulator with the question, the plain answer and the code
 * findings as context. The person still chooses every routing field and
 * confirms every Intent slot: the hand-off is a hint, never an answer
 * (FogBugz parity principle: routing is never inferred).
 *
 * How it travels:
 *
 * 1. The answer builds the payload with {@link buildAskHandoff} and navigates
 *    to the Formulator with {@link askHandoffNavigationState} as the
 *    react-router navigation state, never the URL, so long text and file
 *    paths stay out of the address and the history list.
 * 2. The Formulator page reads it back with {@link readAskHandoffState},
 *    shows it as a read-only card above the routing screen, and passes it to
 *    the Toolbox with `fogbugzFormulator.start` (`priorFindings`), which puts
 *    it in the opening turn's `turn.json`.
 *
 * Nothing about the hand-off is sent to SpecTree: the session SpecTree stores
 * is created from the routing choice alone, and the hand-off lives in page
 * memory and the Toolbox's turn folder on the person's computer.
 */
import { z } from "zod";
import { groomingProjectNameSchema } from "../grooming/api.js";
import { ASK_ANSWER_MAX, ASK_CODE_REFS_MAX, ASK_SPEC_AGREEMENTS, ASK_VERDICTS, } from "./constants.js";
import { askCodeRefSchema, askQuestionSchema, filterAskCodeRefs } from "./types.js";
/** Where a hand-off came from. Only Product Q&A today. */
export const ASK_HANDOFF_SOURCE = "product-qa";
/**
 * The key the hand-off sits under in react-router's navigation state
 * (`location.state[ASK_HANDOFF_STATE_KEY]`).
 */
export const ASK_HANDOFF_STATE_KEY = "spectreeAskHandoff";
const fogbugzNameSchema = z.string().trim().min(1).max(256);
/**
 * Which product the question was about, for display only ("Asked about
 * iCentral NT"). It must never pre-select a FogBugz routing field: the person
 * chooses Project, Area and every other field themselves (FogBugz parity
 * principle, Constraint 5).
 */
export const askHandoffProductHintSchema = z
    .object({
    adoProject: groomingProjectNameSchema.optional(),
    fogbugzProject: fogbugzNameSchema.optional(),
    fogbugzArea: fogbugzNameSchema.optional(),
})
    .strict();
/**
 * The hand-off: the person's question, the plain answer (marker stripped),
 * its verdict and `specAgrees`, and the "Where in the code" references as
 * code findings (paths relative to a repository root, never absolute). Caps
 * reuse Product Q&A's own: `ASK_QUESTION_MAX`, `ASK_ANSWER_MAX`,
 * `ASK_CODE_REFS_MAX`.
 *
 * Everything here is untrusted context for the Formulator's Claude: hints to
 * confirm with the person, never filled Intent slots.
 */
export const askHandoffSchema = z
    .object({
    source: z.literal(ASK_HANDOFF_SOURCE),
    question: askQuestionSchema,
    answer: z
        .string()
        .min(1, "answer must not be empty")
        .max(ASK_ANSWER_MAX, `answer must be at most ${String(ASK_ANSWER_MAX)} characters`)
        .refine((value) => value.trim().length > 0, "answer must not be blank"),
    verdict: z.enum(ASK_VERDICTS),
    specAgrees: z.enum(ASK_SPEC_AGREEMENTS).optional(),
    codeFindings: z.array(askCodeRefSchema).max(ASK_CODE_REFS_MAX),
    productHint: askHandoffProductHintSchema,
})
    .strict();
/**
 * True when an answer should offer the hand-off prominently: the code and
 * the written spec disagree (`specAgrees: "no"`), or nobody wrote down the
 * intended behaviour (`code-only`).
 */
export function isAskHandoffProminent(verdict, specAgrees) {
    return verdict === "code-only" || specAgrees === "no";
}
/** The display-only product hint for a question's scope. */
export function askHandoffProductHint(scope) {
    if (scope.kind === "ado-project")
        return { adoProject: scope.project };
    const hint = { fogbugzProject: scope.fogbugzProject, adoProject: scope.adoProject };
    if (scope.fogbugzArea !== undefined)
        hint.fogbugzArea = scope.fogbugzArea;
    return hint;
}
/**
 * Build the hand-off for one answer: the answer trimmed and cut to
 * `ASK_ANSWER_MAX`, invalid code references dropped (at most
 * `ASK_CODE_REFS_MAX`), `specAgrees` kept only on a `spec-found` answer.
 * Returns null when the result does not parse (an empty question or answer),
 * so the caller hides the button rather than sending something broken.
 */
export function buildAskHandoff(input) {
    const answer = input.answer.trim().slice(0, ASK_ANSWER_MAX);
    const candidate = {
        source: ASK_HANDOFF_SOURCE,
        question: input.question.trim(),
        answer,
        verdict: input.verdict,
        codeFindings: filterAskCodeRefs(input.codeRefs),
        productHint: askHandoffProductHint(input.scope),
    };
    if (input.verdict === "spec-found" && input.specAgrees !== undefined)
        candidate.specAgrees = input.specAgrees;
    const parsed = askHandoffSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
}
/** The navigation state that carries `handoff`: `{ [ASK_HANDOFF_STATE_KEY]: handoff }`. */
export function askHandoffNavigationState(handoff) {
    return { [ASK_HANDOFF_STATE_KEY]: handoff };
}
/**
 * Read the hand-off from a react-router `location.state`. Returns null when
 * there is none or it does not parse with {@link askHandoffSchema}: an
 * invalid hand-off is ignored, never half-used.
 */
export function readAskHandoffState(state) {
    if (typeof state !== "object" || state === null)
        return null;
    const raw = state[ASK_HANDOFF_STATE_KEY];
    if (raw === undefined)
        return null;
    const parsed = askHandoffSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=handoff.js.map